import React, { useState, useEffect } from 'react';
import { Modal, Row, Col, Divider } from 'antd';
import PropTypes from 'prop-types';

import { FormList } from '../../../../modules';
import Popconfirm from 'components/PopConfirm';
import Button from 'components/Button';
import CloseIcon from 'components/Icons/CloseIcon';
import { defaultFormFields } from 'pages/Proposal/constants/constants';

import './FormEditModal.scss';
import utils from 'utils/utils';

const FormEditModal = ({
  prop,
  formEdit,
  visible,
  onCancel,
  onConfirm,
  savingRichEditorModal,
  wixSiteProperties,
  configText,
}) => {
  const [delivs, setDelivs] = useState([]);
  const [tempProp, setTempProp] = useState({});
  const [chosenDelivs, setChosenDelivs] = useState([]);
  const [modifiedFormTitle, setModifiedFormTitle] = useState('');

  const [showPropModal, setShowPropModal] = useState(visible);
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [disableSaveButton, setDisableSaveButton] = useState(false);
  const [twoColumns, setTwoColumns] = useState(formEdit?.config?.columns === 2);
  const isRtl = utils.isRTLProposal(prop);

  // Helper to get the current form title value
  const getCurrentFormTitle = () => {
    if (modifiedFormTitle) {
      return modifiedFormTitle.trim();
    }
    let formTitleObj = formEdit?.form.find((elem) => elem.head);
    if (formTitleObj?.key) {
      return formTitleObj.key?.trim();
    }
    return configText('Please fill the form below', true);
  };

  // Disable save if no fields or no title
  useEffect(() => {
    const title = getCurrentFormTitle();
    if (!title || chosenDelivs.length === 0) {
      setDisableSaveButton(true);
    } else {
      setDisableSaveButton(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modifiedFormTitle, chosenDelivs]);

  const handleKeyCommand = (e) => {
    if (e.key === 'Escape') {
      setShowModal(true);
      const isNotCombinedKey = !(e.ctrlKey || e.altKey || e.shiftKey);
      if (isNotCombinedKey) {
        makeEscapeModal(true);
      }
    }
  };

  const makeEscapeModal = (showModal) => {
    setShowPromptModal(showModal);
    setShowModal(showModal);
  };

  useEffect(() => {
    if (formEdit.isEdit) {
      setChosenDelivs(formEdit.form);
    }
  }, [formEdit]);

  useEffect(() => {
    // For forms, we don't need pricing logic - we handle form fields differently
    const form = formEdit?.form;
    setTempProp({
      formFields: form?.filter((field) => field.selected && field?.head !== true) || [],
    });

    // Initialize chosenDelivs based on existing formFields
    if (form && form.length > 0) {
      const selectedFields = form.filter((field) => field.selected && field?.head !== true);
      if (selectedFields.length > 0) {
        setChosenDelivs(selectedFields);
        setDisableSaveButton(false);
      } else {
        setChosenDelivs([]);
        setDisableSaveButton(true);
      }
    } else {
      setChosenDelivs([]);
      setDisableSaveButton(true);
    }

    if (!showPromptModal) {
      window.addEventListener('keydown', handleKeyCommand, true);
    } else {
      window.removeEventListener('keydown', handleKeyCommand, true);
    }
    setShowPromptModal(!showPromptModal);
  }, []); //eslint-disable-line

  useEffect(() => {
    // Convert defaultFormFields to the format expected by FormList
    let tempDelivs = [];

    tempDelivs = defaultFormFields
      .filter((field) => !field.head)
      .map((field) => {
        return { key: field.key, editable: false };
      });

    tempDelivs.push({ key: 'Custom Field', type: 'custom', multiple: true });
    tempDelivs.push({ key: 'Checkboxes', type: 'checkbox', multiple: true });
    tempDelivs.push({ key: 'Radio button', type: 'radio', multiple: true });

    setDelivs(tempDelivs);
  }, []); // eslint-disable-line

  const changeOptionVal = (chosen, orderSave) => {
    const tempprop = tempProp;

    setDisableSaveButton(false);

    // Store the chosen array directly
    tempprop.formFields = chosen;

    setTempProp(tempprop);
    setChosenDelivs(chosen);
  };

  const mergeFormTitleAndSubmit = () => {
    const tempPropValues = tempProp.formFields || [];
    if (modifiedFormTitle) {
      let formIndex = tempPropValues?.findIndex((elem) => elem.head);
      if (formIndex >= 0) {
        tempPropValues[formIndex].key = modifiedFormTitle;
        tempPropValues[formIndex].custom = true;
      } else {
        tempPropValues.push({
          key: modifiedFormTitle,
          custom: true,
          head: true,
        });
      }
    }

    onConfirm(tempPropValues, { ...formEdit.config, columns: twoColumns ? 2 : 1 });
  };

  let formTitle = formEdit?.form.find((elem) => elem.head);
  if (formTitle) {
    if (formTitle.custom) {
      formTitle = formTitle.key;
    } else {
      formTitle = configText('Please fill the form below', true);
    }
  } else {
    formTitle = configText('Please fill the form below', true);
  }

  return (
    <>
      <Modal
        visible={showPropModal}
        wrapClassName={`form-edit-modal rich-editor-price-edit rich-editor-price-edit-${prop?.language?.toLowerCase()}`}
        title={
          <div className="rich-editor-price-edit-header">
            <h3 className="title">
              <span>{formEdit.isEdit ? 'Edit Form' : 'Add Form'}</span>
            </h3>
            <Divider />
          </div>
        }
        closeIcon={<CloseIcon />}
        okText="OK"
        cancelText="CLOSE"
        onCancel={() => setShowModal(true)}
        cancelButtonProps={{ disabled: savingRichEditorModal }}
        maskClosable={false}
        closable={!savingRichEditorModal}
        onOk={mergeFormTitleAndSubmit}
        footer={[
          <Col className="rich-editor-modal-footer" key="rich-editor-modal-footer">
            <Row className="note-row">
              <span>
                <b>Keep it safe:</b> don’t ask for private details such as credit card or Social
                Security number.
              </span>
            </Row>
            <Row className="button-row" justify="end" align="middle" style={{ width: '100%' }}>
              <Button
                className="large-btn grey-btn"
                text="CANCEL"
                onClick={() => setShowModal(true)}
              />
              <Button
                className={`large-btn secondary-btn 
                ${disableSaveButton && 'rich-editor-modal-continue-disabled'}`}
                text={savingRichEditorModal ? 'SAVING' : 'ADD'}
                onClick={mergeFormTitleAndSubmit}
                disabled={savingRichEditorModal || disableSaveButton}
                loading={savingRichEditorModal}
              />
            </Row>
          </Col>,
        ]}>
        <Divider className="below-xl" style={{ margin: '0px' }} />
        <FormList
          formFields={delivs}
          chosen={chosenDelivs || []}
          onChange={(chosen, orderSave) => {
            changeOptionVal(chosen, orderSave);
          }}
          config={formEdit.config}
          configText={configText}
          isRtl={isRtl}
          formTitle={formTitle}
          modifiedFormTitle={modifiedFormTitle}
          setModifiedFormTitle={setModifiedFormTitle}
          twoColumns={twoColumns}
          setTwoColumns={setTwoColumns}
        />
      </Modal>
      <>
        {showModal && (
          <Popconfirm
            visible={showModal}
            title={'Do you want to exit without saving?'}
            maskClosable={!savingRichEditorModal}
            closable={false}
            cancelText={'Discard'}
            confirmText={
              savingRichEditorModal ? 'Saving...' : !disableSaveButton ? 'Save Changes' : ''
            }
            onCancel={() => {
              setShowModal(false);
              setShowPropModal(true);
              onCancel();
            }}
            onConfirm={() => {
              !disableSaveButton && mergeFormTitleAndSubmit();
            }}
            footer={null}
            closeAction={() => {
              setShowModal(false);
              setShowPropModal(true);
            }}
          />
        )}
      </>
    </>
  );
};

FormEditModal.defaultProps = {
  savingRichEditorModal: false,
};

FormEditModal.propTypes = {
  prop: PropTypes.instanceOf(Object).isRequired,
  config: PropTypes.instanceOf(Object).isRequired,
  onCancel: PropTypes.func.isRequired,
  visible: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  formEdit: PropTypes.instanceOf(Object).isRequired,
  removeItem: PropTypes.func.isRequired,
  saveUser: PropTypes.func.isRequired,
  savingRichEditorModal: PropTypes.bool,
};

export default FormEditModal;
